import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ATTENTION_INBOX_QUERY_ROOTS } from "@/lib/api/attention-cache";
import {
	type ConnectionItem,
	type ConnectorDefinitionItem,
	useAuthProfiles,
	useAvailableOperations,
	useConnectConnection,
	useFeeds,
	useReauthenticateConnection,
} from "@/lib/api/connections";
import { useAuthState } from "@/lib/auth-state";
import {
	getOAuthMethod,
	getOptionalOAuthScopes,
	getRequiredOAuthScopes,
} from "./auth-helpers";

export function OAuthScopeSelector({
	requiredScopes,
	optionalScopes,
	selectedOptionalScopes,
	grantedScopes = [],
	onToggleOptionalScope,
}: {
	requiredScopes: string[];
	optionalScopes: string[];
	selectedOptionalScopes: string[];
	grantedScopes?: string[];
	onToggleOptionalScope: (scope: string, checked: boolean) => void;
}) {
	const scopeFieldId = useId();
	if (!requiredScopes.length && !optionalScopes.length && !grantedScopes.length)
		return null;
	return (
		<div className="space-y-3 text-sm">
			{grantedScopes.length > 0 && (
				<div>
					<p className="font-medium">Already granted</p>
					<p className="text-muted-foreground">
						These permissions are included when you authorize again.
					</p>
					<ul className="mt-1 space-y-1">
						{grantedScopes.map((scope) => (
							<li className="break-all" key={scope}>
								{scope}
							</li>
						))}
					</ul>
				</div>
			)}
			{requiredScopes.some((scope) => !grantedScopes.includes(scope)) && (
				<div>
					<p className="font-medium">Required permissions</p>
					<ul className="mt-1 space-y-1">
						{requiredScopes
							.filter((scope) => !grantedScopes.includes(scope))
							.map((scope) => (
								<li className="break-all" key={scope}>
									{scope}
								</li>
							))}
					</ul>
				</div>
			)}
			{optionalScopes.some((scope) => !grantedScopes.includes(scope)) && (
				<fieldset className="space-y-2">
					<legend className="font-medium">Additional permissions</legend>
					{optionalScopes
						.filter((scope) => !grantedScopes.includes(scope))
						.map((scope) => (
							<div key={scope} className="flex items-start gap-2">
								<Checkbox
									id={`${scopeFieldId}-${scope}`}
									className="mt-0.5"
									checked={selectedOptionalScopes.includes(scope)}
									onCheckedChange={(value) =>
										onToggleOptionalScope(scope, value === true)
									}
								/>
								<label
									htmlFor={`${scopeFieldId}-${scope}`}
									className="min-w-0 cursor-pointer break-all"
								>
									{scope}
								</label>
							</div>
						))}
				</fieldset>
			)}
		</div>
	);
}

function refreshOAuthState(queryClient: QueryClient) {
	return queryClient.invalidateQueries({
		predicate: (query) =>
			query.queryKey.some((key) =>
				[
					"auth-profiles",
					"connections",
					"feeds",
					"available-operations",
					"connector-operations",
					...ATTENTION_INBOX_QUERY_ROOTS,
				].includes(String(key)),
			),
	});
}

export function OAuthReturnNotice({
	result,
}: {
	result?: "connected" | "cancelled" | "failed";
}) {
	const queryClient = useQueryClient();
	useEffect(() => {
		if (result) void refreshOAuthState(queryClient);
	}, [result, queryClient]);
	if (!result) return null;
	return (
		<p
			role={result === "failed" ? "alert" : "status"}
			className="rounded-md border p-3 text-sm"
		>
			{result === "connected"
				? "Account authorization updated. Review your permissions below."
				: result === "cancelled"
					? "Authorization cancelled. No permissions were added."
					: "Authorization did not finish. Review your account below to try again."}
		</p>
	);
}

function useOAuthPermissions(
	connection: ConnectionItem,
	connectorDef?: ConnectorDefinitionItem,
) {
	const { data: profiles = [] } = useAuthProfiles(connection.organization_id, {
		connectorKey: connection.connector_key,
	});
	const {
		data: operations = [],
		isLoading,
		isError,
	} = useAvailableOperations(connection.id);
	const { data: feeds = [] } = useFeeds(connection.id);
	const profile = profiles.find(
		(item) => item.slug === connection.auth_profile_slug,
	);
	const method = getOAuthMethod(connectorDef?.auth_schema);
	const affected = operations
		.filter((operation) => operation.readiness === "scope_upgrade_required")
		.map((operation) => ({
			key: operation.operation_key,
			name: operation.name,
			scopes: operation.next_action?.requested_scopes ?? [],
		}));
	if (profile?.granted_scopes) {
		const definitions = Object.entries(connectorDef?.feeds_schema ?? {});
		for (const feed of feeds) {
			const definition = definitions.find(
				([key, value]) =>
					key === feed.feed_key ||
					(value as { key?: string })?.key === feed.feed_key,
			)?.[1] as { requiredScopes?: string[] } | undefined;
			const missing = (definition?.requiredScopes ?? []).filter(
				(scope) => !profile.granted_scopes?.includes(scope),
			);
			if (missing.length)
				affected.push({
					key: `feed:${feed.id}`,
					name: feed.display_name ?? feed.feed_key,
					scopes: missing,
				});
		}
	}
	return {
		profile,
		method,
		affected,
		missing: [...new Set(affected.flatMap((item) => item.scopes))],
		isLoading,
		isError,
	};
}

export function ConnectionPermissionNotice({
	connection,
	connectorDef,
	onReview,
}: {
	connection: ConnectionItem;
	connectorDef?: ConnectorDefinitionItem;
	onReview: () => void;
}) {
	const { affected } = useOAuthPermissions(connection, connectorDef);
	const affectedFeeds = affected.filter((item) => item.key.startsWith("feed:"));
	if (!affectedFeeds.length || connection.status !== "active") return null;
	return (
		<div className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3">
			<div className="min-w-0 text-sm">
				<p className="font-medium">Some sources need additional permissions</p>
				<p className="text-muted-foreground">
					{affectedFeeds
						.slice(0, 3)
						.map((operation) => operation.name)
						.join(", ")}
					{affectedFeeds.length > 3
						? ` and ${affectedFeeds.length - 3} more`
						: ""}
				</p>
			</div>
			<Button variant="outline" size="sm" onClick={onReview}>
				Review permissions
			</Button>
		</div>
	);
}

/** The same browser handoff for new accounts, profile recovery and connection recovery. */
export function useOAuthAuthorization() {
	const queryClient = useQueryClient();
	const cleanup = useRef<(() => void) | undefined>(undefined);
	const [pending, setPending] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [continueUrl, setContinueUrl] = useState<string | null>(null);
	useEffect(() => () => cleanup.current?.(), []);
	const begin = async (
		authorize: () => Promise<{
			connect_url?: string;
			setup_url?: string;
			instructions?: string;
			status?: string;
		}>,
	) => {
		cleanup.current?.();
		const popup = window.open("about:blank", "_blank");
		if (popup) popup.opener = null;
		let active = true;
		cleanup.current = () => {
			active = false;
		};
		setPending(true);
		setError(null);
		setContinueUrl(null);
		try {
			const result = await authorize();
			if (!active) {
				popup?.close();
				return;
			}
			const url = result.connect_url;
			if (!url) {
				popup?.close();
				if (result.status === "active") {
					void refreshOAuthState(queryClient);
					return;
				}
				setError(
					result.instructions ??
						"Authorization could not start. Review the app configuration and try again.",
				);
				setContinueUrl(result.setup_url ?? null);
				return;
			}
			setContinueUrl(url);
			const refresh = () => {
				void refreshOAuthState(queryClient);
				if (!popup || popup.closed) {
					cleanup.current?.();
					setContinueUrl(null);
				}
			};
			const interval = popup
				? window.setInterval(() => {
						if (popup.closed) refresh();
					}, 500)
				: undefined;
			window.addEventListener("focus", refresh);
			cleanup.current = () => {
				active = false;
				window.removeEventListener("focus", refresh);
				if (interval) window.clearInterval(interval);
			};
			if (popup) popup.location.href = url;
		} catch (cause) {
			popup?.close();
			if (!active) return;
			setError(
				cause instanceof Error
					? cause.message
					: "Authorization could not start. Try again.",
			);
		} finally {
			if (active) setPending(false);
		}
	};
	return { begin, pending, error, continueUrl };
}

export function ConnectionOAuthAuthorization({
	connection,
	connectorDef,
}: {
	connection: ConnectionItem;
	connectorDef: ConnectorDefinitionItem;
}) {
	const { profile, method, affected, missing, isLoading, isError } =
		useOAuthPermissions(connection, connectorDef);
	const { session } = useAuthState();
	const reconnect = useReauthenticateConnection();
	const connect = useConnectConnection();
	const flow = useOAuthAuthorization();
	const [review, setReview] = useState(false);
	const [selected, setSelected] = useState<string[]>([]);
	const mayAuthorize =
		!!session?.user?.id &&
		session.user.id === connection.created_by &&
		(!connection.auth_profile_slug ||
			profile?.created_by === session?.user?.id);
	if (!method) return null;
	const feedCount = affected.filter((item) =>
		item.key.startsWith("feed:"),
	).length;
	const actionCount = affected.length - feedCount;
	const affectedSummary = [
		feedCount
			? `${feedCount} configured ${feedCount === 1 ? "feed" : "feeds"}`
			: null,
		actionCount
			? `${actionCount} ${actionCount === 1 ? "action" : "actions"}`
			: null,
	]
		.filter(Boolean)
		.join(" and ");
	const authorize = (scopes?: string[]) =>
		connection.auth_profile_slug
			? reconnect.mutateAsync({
					connection_id: connection.id,
					requested_scopes: scopes,
				})
			: connect.mutateAsync({
					connector_key: connection.connector_key,
					slug: connection.slug,
					app_auth_profile_slug: connection.app_auth_profile_slug ?? undefined,
					requested_scopes: scopes,
				});
	return (
		<div className="space-y-3">
			{affected.length > 0 && (
				<p role="status" className="text-sm text-muted-foreground">
					{affectedSummary} {affected.length === 1 ? "requires" : "require"}{" "}
					additional permissions from {connectorDef.name || method.provider}.
				</p>
			)}
			{isLoading && (
				<p className="text-sm text-muted-foreground">Checking permissions…</p>
			)}
			{isError && (
				<p role="alert" className="text-sm text-destructive">
					Could not check capability permissions. Refresh and try again.
				</p>
			)}
			{mayAuthorize ? (
				<div className="flex flex-wrap gap-2">
					<Button
						variant="outline"
						size="sm"
						disabled={flow.pending}
						onClick={() => void flow.begin(() => authorize())}
					>
						{connection.auth_profile_slug ? "Reconnect" : "Connect account"}
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={() => {
							setReview(!review);
							setSelected(missing);
						}}
					>
						Review permissions
					</Button>
				</div>
			) : (
				<p className="text-sm text-muted-foreground">
					Only the account owner can reconnect this account.
				</p>
			)}
			{review && mayAuthorize && (
				<div className="space-y-3">
					{affected.length > 0 && (
						<div>
							<p className="text-sm font-medium">
								{feedCount
									? "Feeds and actions needing access"
									: "Actions needing access"}
							</p>
							<ul className="mt-1 list-disc pl-5 text-sm text-muted-foreground">
								{affected.map((item) => (
									<li key={item.key}>{item.name}</li>
								))}
							</ul>
						</div>
					)}
					<OAuthScopeSelector
						requiredScopes={getRequiredOAuthScopes(method)}
						optionalScopes={getOptionalOAuthScopes(method)}
						selectedOptionalScopes={selected}
						grantedScopes={profile?.granted_scopes}
						onToggleOptionalScope={(scope, checked) =>
							setSelected((current) =>
								checked
									? [...new Set([...current, scope])]
									: current.filter((value) => value !== scope),
							)
						}
					/>
					<Button
						size="sm"
						disabled={flow.pending}
						onClick={() => void flow.begin(() => authorize(selected))}
					>
						Continue to {method.provider}
					</Button>
				</div>
			)}
			{flow.error && (
				<p role="alert" className="text-sm text-destructive">
					{flow.error}
				</p>
			)}
			{flow.continueUrl && (
				<a
					className="text-sm underline"
					href={flow.continueUrl}
					target="_blank"
					rel="noreferrer"
				>
					Continue authorization in your browser
				</a>
			)}
		</div>
	);
}
